import { Box, BoxProps, Typography, TypographyProps } from "@mui/material";
import { FormikErrors } from "formik";
import { visuallyHidden } from "@mui/utils";

/**
 * @returns Semantic and styled HTML element for displaying a required field indicator
 */
export const RequiredIndicator = () => (
  <Typography
    aria-label="required"
    component="span"
    sx={{ color: "error.main", fontWeight: "bold" }}
    title="Required"
  >
    *
  </Typography>
);

/**
 * Displays an error message for a form field if it has an error
 * @param props Includes the field name and the errors object from Formik
 * @returns A caption element with the error message
 */
export const FormError = <T,>(props: { fieldName: keyof T; errors: FormikErrors<T>; id: string }) => {
  if (!props.errors[props.fieldName]) {
    return <></>;
  }
  return (
    <Typography variant="caption" color="error" sx={{ mt: 1 }} id={props.id}>
      <>{props.errors[props.fieldName]}</>
    </Typography>
  );
};

/**
 * Displays an error message using the MUI Typography component, provides a consistent look and feel with ability to override props
 * @param props Includes the error message and optional TypographyProps
 * @returns A caption element with the error message
 */
export const ErrorMessage = (props: TypographyProps & { message: string }) => {
  const { message, sx, ...other } = props;
  const typoProps = {
    variant: "caption",
    sx: { pt: 1, ...sx },
    ...other,
    color: "error",
  } as TypographyProps;
  return <Typography {...typoProps}>{message}</Typography>;
};

/**
 * Used to easily wrap a group of form fields in a fieldset with a legend
 *
 * @param props MUI BoxProps + legend
 * @returns A <fieldset> with a <legend> that is visually hidden
 */
export function Fieldset(props: BoxProps & { legend: string }) {
  const { legend, children, sx, ...other } = props;
  return (
    <Box
      {...other}
      component="fieldset"
      sx={{
        ...sx,
        border: 0,
        p: 0,
        m: 0,
      }}
    >
      <legend style={visuallyHidden}>{legend}</legend>
      {children}
    </Box>
  );
}
